import {
  DATA_SCOPE,
  USER_END,
  USER_TYPE,
  USER_TYPE_META,
  labelOf,
  normalizeEnum,
  normalizeEnumOrNull,
  type DataScope,
  type UserEnd,
  type UserType,
} from '../enums';
import type { CurrentUser, JwtClaims } from '../types/models';
import { recordDrift } from '../http/client';

/**
 * JWT 声明解析（**只读声明，不验签**）：后端 §6.0-② 用 JWT Bearer，联调实测声明含
 * `sub / account / name / user_type / end / role / exp`。
 *
 * 用途：§6.2-7 `GET /auth/profile` 已冻结为**权限快照的权威来源**；本模块只在登录响应本身
 * 不含身份快照时充当 interim 身份（数据来自本次登录的服务端响应，不是 localStorage 陈旧态，
 * 符合 §3.5-② 意图）。权限码不在声明里，故 `permissions` 为空、仅由 `roleCode === SUPER_ADMIN`
 * 走内置超管通配，profile 恢复后即覆盖；`exp` 始终用于会话倒计时。
 */
export function parseJwtClaims(token: string | null | undefined): JwtClaims | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const payload = base64UrlDecode(parts[1]);
    const claims = JSON.parse(payload);
    return claims && typeof claims === 'object' ? (claims as JwtClaims) : null;
  } catch {
    return null;
  }
}

function base64UrlDecode(input: string): string {
  const normalized = input.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  // 声明含中文（如 name=超级管理员），必须按 UTF-8 解码
  return new TextDecoder('utf-8').decode(bytes);
}

const USER_TYPE_CODES = Object.values(USER_TYPE) as UserType[];
const USER_END_CODES = Object.values(USER_END) as UserEnd[];

/** 令牌/快照里的 user_type → 封闭集枚举值（大小写宽松，未命中回落 OPS_ADMIN） */
export function asUserType(value: unknown): UserType {
  return normalizeEnum(value, USER_TYPE_CODES, USER_TYPE.OPS_ADMIN);
}

/** 令牌/快照里的端标识 → 封闭集枚举值（未命中返回 null，不伪造合法端） */
export function asEnd(value: unknown): UserEnd | null {
  return normalizeEnumOrNull(value, USER_END_CODES);
}

/**
 * 线上 `endBinding` 数组的**入口一次性归一**（seq322 §六 裁定）：权威值是全大写
 * `ADMIN` / `BPO` / `MINIAPP`（《数据库设计 v2.0》line 41 `user_end` 枚举，`sys_role.end_binding`
 * 即其数组投影）；后端文档 §6.2(7) 的小写写法是文档笔误，不改后端、也不在前端保留双向兼容。
 *
 * 因此这里归一后**只向下游传大写值**；观测到小写说明线上仍未修正，记一次契约漂移暴露出来，
 * 而不是静默吸收成长期技术债。未知端值忽略，不伪造合法端。
 */
export function normalizeEnds(values: readonly unknown[] | null | undefined): UserEnd[] {
  const ends: UserEnd[] = [];
  for (const raw of values ?? []) {
    const end = asEnd(raw);
    if (!end) {
      console.warn(`[contract-drift] endBinding 未知端值 ${String(raw)}（不在 USER_END 封闭集），已忽略`);
      continue;
    }
    if (String(raw) !== end) {
      recordDrift('endBinding-lowercase', String(raw));
      console.warn(`[contract-drift] endBinding 下发 ${String(raw)}，seq322 §六 已裁定权威值为大写 ${end}`);
    }
    if (!ends.includes(end)) ends.push(end);
  }
  return ends;
}

/** 令牌 exp（秒）→ 毫秒时间戳；无 exp 返回 0 */
export function claimsExpiresAt(claims: JwtClaims | null): number {
  return claims && typeof claims.exp === 'number' ? claims.exp * 1000 : 0;
}

/** 由服务端声明重建身份快照（权限码待 §6.2-7 profile 补齐） */
export function userFromJwtClaims(claims: JwtClaims): CurrentUser {
  const account = String(claims.account ?? claims.name ?? '');
  const name = String(claims.name ?? account);
  const userType = asUserType(claims.user_type);
  const end = asEnd(claims.end);
  const roleCode = String(claims.user_type ?? claims.role ?? '');
  return {
    id: String(claims.sub ?? account),
    account,
    name,
    userType,
    userTypeLabel: labelOf(USER_TYPE_META, userType),
    roleCode,
    roleName: name,
    regionCodes: [],
    endBinding: end ? [end] : [],
    // 实测部署版令牌带 data_scope；缺失/未知按最小权限落位，绝不猜 ALL（权威值待 /auth/profile）
    dataScope: normalizeEnumOrNull(claims.data_scope, Object.values(DATA_SCOPE) as DataScope[]) ?? DATA_SCOPE.SELF_ONLY,
    permissions: [],
    permVersion: 0,
    avatarText: name.slice(0, 1),
  };
}
